import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthCookiesService } from './auth-cookies.service';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { ToastType } from '../../enums/toast-type.enum';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    constructor(
        private readonly cookiesService: AuthCookiesService,
        private readonly authService: AuthService,
        private readonly toastService: ToastService,
        private readonly msal: MsalService
    ) {}

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const idToken = this.cookiesService.getIdTokenFromCookie();

        if (idToken) {
            const clonedRequest = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${idToken}`,
                },
            });

            return next.handle(clonedRequest).pipe(catchError((error) => this.handleError(error, req, next)));
        }

        return next.handle(req).pipe(catchError((error) => this.handleError(error, req, next)));
    }

    private handleError(error: HttpErrorResponse, req: HttpRequest<unknown>, next: HttpHandler) {
        if (error.status === 401 || error.status === 403) {
            return this.silentLogin().pipe(
                // Retry the failed request after the silent login succeeds
                catchError((silentLoginError) => {
                    this.handleUnauthorizedError();
                    return throwError(silentLoginError);
                }),
                switchMap((account) => {
                    if (account.idToken) {
                        this.cookiesService.setIdTokenCookie(account.idToken);
                    }
                    const clonedRequest = req.clone({
                        setHeaders: {
                            Authorization: `Bearer ${this.cookiesService.getIdTokenFromCookie()}`,
                        },
                    });

                    return next.handle(clonedRequest);
                })
            );
        }

        return throwError(error);
    }

    private silentLogin(): Observable<AuthenticationResult> {
        return this.msal.acquireTokenSilent({
            scopes: ['openid', 'profile', 'email'],
        });
    }

    private async handleUnauthorizedError() {
        this.toastService.showToast('Your session has expired or you are not authorized to access this page. Please sign in again.', ToastType.Warning);
        await this.authService.logOut();
    }
}
